/**
 * ControlPanel API (`/users`) クライアント (権限管理 SBI-8)。
 *
 * ユーザーの実体は Cognito (WebAppUserPool)。ここでは一覧 / 招待 / 無効化 / 再有効化 / 削除と、
 * 権限付与 (ユーザーごとの所属グループの取得・置き換え) を扱う。
 *
 * ⚠️ 最終的な認可は常にバックエンド (403)。画面の出し分けは利便性のため。
 */
import { adminRequest, encodeSegment, type AdminClientOptions } from './adminRequest';
import type {
  AdminUser,
  AdminUserDeleteResponse,
  AdminUserListResponse,
  UserPermissions,
} from './types';

export type UsersClientOptions = AdminClientOptions;

export class UsersClient {
  private readonly options: UsersClientOptions;

  constructor(options: UsersClientOptions) {
    this.options = options;
  }

  private userPath(userSub: string, suffix = ''): string {
    return `users/${encodeSegment(userSub)}${suffix}`;
  }

  /** GET /users (メールアドレス順。所属グループ付き) */
  async list(): Promise<AdminUserListResponse> {
    return adminRequest<AdminUserListResponse>(this.options, 'GET', 'users');
  }

  /** POST /users (招待。Cognito が一時パスワード付きの招待メールを送る) */
  async invite(email: string): Promise<AdminUser> {
    return adminRequest<AdminUser>(this.options, 'POST', 'users', { email });
  }

  /** POST /users/{user_sub}/disable (冪等。自分自身 / 最後の管理者は拒否される) */
  async disable(userSub: string): Promise<AdminUser> {
    return adminRequest<AdminUser>(this.options, 'POST', this.userPath(userSub, '/disable'), {});
  }

  /** POST /users/{user_sub}/enable (冪等) */
  async enable(userSub: string): Promise<AdminUser> {
    return adminRequest<AdminUser>(this.options, 'POST', this.userPath(userSub, '/enable'), {});
  }

  /** DELETE /users/{user_sub} (自分自身 / 最後の管理者は拒否される) */
  async remove(userSub: string): Promise<AdminUserDeleteResponse> {
    return adminRequest<AdminUserDeleteResponse>(this.options, 'DELETE', this.userPath(userSub));
  }

  /** GET /users/{user_sub}/permission-groups (所属グループ + 利用可能な機能) */
  async getPermissionGroups(userSub: string): Promise<UserPermissions> {
    return adminRequest<UserPermissions>(
      this.options,
      'GET',
      this.userPath(userSub, '/permission-groups'),
    );
  }

  /** PUT /users/{user_sub}/permission-groups (所属グループを `groupIds` で置き換える。空配列は全解除) */
  async setPermissionGroups(userSub: string, groupIds: string[]): Promise<UserPermissions> {
    return adminRequest<UserPermissions>(
      this.options,
      'PUT',
      this.userPath(userSub, '/permission-groups'),
      { group_ids: groupIds },
    );
  }
}
